import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Trash2, ChevronDown } from "lucide-react";
import cn from "classnames";
import { Input } from "campaign-builder/ui";
import { apiClient } from "module/api";
import { updateCampaign } from "graphQL/mutations/campaign";
import { RadioDot, RequiredLabel, FieldError } from "./fields";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useCountryAddressConfig } from "campaign-builder/checkout/useCountryAddressConfig";
import { useSaveStatus, trackSave } from "../saveStatus";
import { useTargetingValidation } from "../targetingValidation";

interface ReturnAddressData {
  id: number;
  address: string;
  city: string;
  state: string;
  zipcode: string;
}

const validateAddress = (value: string): string | undefined => {
  if (!value || value.length < 3) return "Address must be at least 3 characters";
  if (!/\d/.test(value)) return "Address must contain at least one number";
  return undefined;
};

const validateCity = (value: string): string | undefined => {
  if (!value || value.trim().length === 0) return "City is required";
  if (/\d/.test(value)) return "City must not contain numbers";
  return undefined;
};

const validateZipcode = (value: string): string | undefined => {
  if (!value) return "ZIP code is required";
  if (!/^\d{5}(-\d{4})?$/.test(value)) return "Enter a valid ZIP code (e.g., 10001 or 10001-1234)";
  return undefined;
};

const StateCombobox = ({
  value,
  onChange,
  error,
  dataCy,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  dataCy?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const { config } = useCountryAddressConfig("US");
  const states = config.regionOptions ?? [];
  const selected = states.find((s) => s.code === value);

  useEffect(() => {
    const onDoc = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const listId = React.useId();
  const pick = (code: string) => {
    onChange(code);
    setQuery("");
    setOpen(false);
  };

  const q = query.toLowerCase();
  const filtered = states.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q));

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-cy={dataCy}
        // ARIA combobox so assistive tech — and Copilot control mode's
        // `ui_select`, which scopes options to the listbox this trigger
        // controls — can see the popover as a dropdown, not loose buttons.
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label="State"
        aria-invalid={error}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-[10px] border border-solid bg-background pl-3 pr-2 text-left text-sm",
          error ? "border-destructive" : "border-border"
        )}
      >
        <span className={selected ? "text-foreground" : "text-muted-foreground"}>
          {selected ? selected.name : "Type or select a state"}
        </span>
        <ChevronDown className="text-muted-foreground h-4 w-4" />
      </button>
      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label="States"
          data-copilot-options="open"
          className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-[10px] border border-solid border-border bg-popover py-1 shadow-lg"
        >
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search…"
            aria-label="Search states"
            data-cy={dataCy ? `${dataCy}Search` : undefined}
            className="mb-1 w-full border-0 border-b border-slate-100 px-3 py-1.5 text-sm outline-none"
          />
          {filtered.map((s) => (
            <button
              key={s.code}
              type="button"
              role="option"
              aria-selected={s.code === value}
              data-cy={dataCy ? `${dataCy}-option-${s.code}` : undefined}
              // mousedown keeps the search input from blurring first (real
              // pointer); click is what keyboards and Copilot's synthetic
              // clicks dispatch — both must pick the state.
              onMouseDown={(event) => {
                event.preventDefault();
                pick(s.code);
              }}
              onClick={() => pick(s.code)}
              className="block w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted"
            >
              {s.name}
            </button>
          ))}
          {filtered.length === 0 && <p className="text-slate-400 px-3 py-1.5 text-sm">No matches</p>}
        </div>
      )}
    </div>
  );
};

const SavedAddressRow = ({
  data,
  selected,
  inUse,
  onSelect,
  onDelete,
}: {
  data: ReturnAddressData;
  selected: boolean;
  inUse: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) => (
  <div
    className={cn(
      "flex items-center gap-3 rounded-lg border-solid p-4",
      selected ? "border-[1.5px] border-[#222336] dark:border-slate-300" : "border border-border"
    )}
  >
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      data-cy={`admReturnAddressSaved-${data.id}`}
      className="flex flex-1 items-center gap-3 text-left"
    >
      <RadioDot selected={selected} />
      <span className="flex flex-col">
        <span className="text-foreground text-sm font-semibold leading-5">{data.address}</span>
        <span className="text-muted-foreground text-xs leading-4">{`${data.city}, ${data.state}, ${data.zipcode}`}</span>
      </span>
    </button>
    <button
      type="button"
      onClick={onDelete}
      disabled={inUse}
      aria-label="Delete return address"
      title={inUse ? "Return address is currently in use by a campaign or workflow" : "Remove"}
      className={cn(
        "shrink-0",
        inUse ? "text-slate-200 cursor-not-allowed" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
      )}
    >
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

export const ReturnAddressSection = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { campaignData, updateCampaignState } = useCampaignData();
  const save = useSaveStatus();
  const { version: validationVersion } = useTargetingValidation();
  const [validationRequested, setValidationRequested] = useState(false);

  const [mode, setMode] = useState<"select" | "new">(campaignData?.returnAddressId ? "select" : "new");
  const [saved, setSaved] = useState<ReturnAddressData[]>([]);
  const [inUseIds, setInUseIds] = useState<number[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(campaignData?.returnAddressId ?? null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (validationVersion > 0) setValidationRequested(true);
  }, [validationVersion]);

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [touched, setTouched] = useState({ address: false, city: false, state: false, zipcode: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    apiClient
      .getAllReturnAddresses()
      .then((response: Response) => (response.ok ? response.json() : { returnAddresses: [] }))
      .then((data: { returnAddresses?: ReturnAddressData[]; inUseIds?: Array<number | string> }) => {
        if (!active) return;
        const list = Array.isArray(data.returnAddresses) ? data.returnAddresses : [];
        setSaved(list);
        setInUseIds(Array.isArray(data.inUseIds) ? data.inUseIds.map((id) => Number(id)).filter(Number.isInteger) : []);
        if (campaignData?.returnAddressId && list.some((a) => a.id === campaignData.returnAddressId)) {
          setMode("select");
          setSelectedId(campaignData.returnAddressId);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const persistSelection = async (id: number | null) => {
    setSelectedId(id);
    if (campaignData) updateCampaignState({ ...campaignData, returnAddressId: id });
    if (campaignId) {
      try {
        await trackSave(save, () => updateCampaign({ campaignId, returnAddressId: id }));
      } catch (error) {
        // keep local selection; linking can be retried
      }
    }
  };

  const errors = {
    address: validateAddress(address),
    city: validateCity(city),
    state: stateCode ? undefined : "State is required",
    zipcode: validateZipcode(zipcode),
  };
  const isFormValid = !errors.address && !errors.city && !errors.state && !errors.zipcode;

  const handleSaveNew = async () => {
    if (!isFormValid || saving) return;
    setSaving(true);
    try {
      let response!: Response;
      await trackSave(save, async () => {
        response = await apiClient.saveReturnAddress({ address, city, state: stateCode, zipcode });
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.returnAddress) {
          const record = data.returnAddress as ReturnAddressData;
          setSaved((prev) => (prev.some((a) => a.id === record.id) ? prev : [record, ...prev]));
          await persistSelection(record.id);
          setMode("select");
          setAddress("");
          setCity("");
          setStateCode("");
          setZipcode("");
          setTouched({ address: false, city: false, state: false, zipcode: false });
        }
      }
    } catch (error) {
      // save failure already surfaced via trackSave's status; avoid an unhandled rejection in onBlur callers
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (inUseIds.includes(id)) return;
    let response!: Response;
    try {
      await trackSave(save, async () => {
        response = await apiClient.deleteReturnAddress(id);
      });
    } catch (error) {
      return;
    }
    // Only drop it locally if the backend actually deleted it — otherwise an
    // in-use/protected address would vanish from the UI and reappear on refetch.
    if (!response?.ok) return;
    setSaved((prev) => prev.filter((a) => a.id !== id));
    setInUseIds((prev) => prev.filter((inUseId) => inUseId !== id));
    if (selectedId === id) persistSelection(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <span className="text-foreground text-sm font-medium leading-5" id="admReturnAddressLabel">
        Return to the following address:
      </span>
      <FieldError
        message={validationRequested && !selectedId ? "Add or select a return address to continue." : undefined}
        id="admReturnAddressError"
      />

      <div
        role="radiogroup"
        aria-labelledby="admReturnAddressLabel"
        aria-describedby={validationRequested && !selectedId ? "admReturnAddressError" : undefined}
        className="flex flex-wrap gap-3"
      >
        <button
          type="button"
          role="radio"
          aria-checked={mode === "select"}
          onClick={() => setMode("select")}
          data-cy="admReturnAddressMode-saved"
          className="flex items-center gap-2 text-left"
        >
          <RadioDot selected={mode === "select"} />
          <span className="text-foreground text-sm font-medium leading-5">Select a saved return address</span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={mode === "new"}
          onClick={() => setMode("new")}
          data-cy="admReturnAddressMode-new"
          className="flex items-center gap-2 text-left"
        >
          <RadioDot selected={mode === "new"} />
          <span className="text-slate-700 dark:text-slate-300 text-sm font-medium leading-5">
            Add a new return address
          </span>
        </button>
      </div>

      {mode === "select" ? (
        <div className="flex flex-col gap-2">
          {loading && <p className="text-muted-foreground text-sm">Loading saved addresses…</p>}
          {!loading && saved.length === 0 && (
            <p className="text-muted-foreground text-sm">No saved addresses yet. Add a new return address.</p>
          )}
          {!loading &&
            saved.map((a) => (
              <SavedAddressRow
                key={a.id}
                data={a}
                selected={selectedId === a.id}
                inUse={inUseIds.includes(a.id)}
                onSelect={() => persistSelection(a.id)}
                onDelete={() => handleDelete(a.id)}
              />
            ))}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <RequiredLabel>Address</RequiredLabel>
            <Input
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              onBlur={() => {
                setTouched((t) => ({ ...t, address: true }));
                handleSaveNew();
              }}
              placeholder="Enter full address (e.g., 123 Main Street, Apt 4B)"
              className={touched.address && errors.address ? "border-destructive" : undefined}
              data-cy="admReturnAddressStreetInput"
              aria-invalid={touched.address && !!errors.address}
              aria-describedby={touched.address && errors.address ? "admReturnAddressStreetError" : undefined}
            />
            <FieldError message={touched.address ? errors.address : undefined} id="admReturnAddressStreetError" />
          </div>
          <div className="flex gap-5">
            <div className="flex flex-1 flex-col gap-1">
              <RequiredLabel>City</RequiredLabel>
              <Input
                value={city}
                onChange={(event) => setCity(event.target.value)}
                onBlur={() => {
                  setTouched((t) => ({ ...t, city: true }));
                  handleSaveNew();
                }}
                placeholder="Enter city (e.g., New York)"
                className={touched.city && errors.city ? "border-destructive" : undefined}
                data-cy="admReturnAddressCityInput"
                aria-invalid={touched.city && !!errors.city}
                aria-describedby={touched.city && errors.city ? "admReturnAddressCityError" : undefined}
              />
              <FieldError message={touched.city ? errors.city : undefined} id="admReturnAddressCityError" />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <RequiredLabel>State</RequiredLabel>
              <StateCombobox
                value={stateCode}
                onChange={(value) => {
                  setStateCode(value);
                  setTouched((t) => ({ ...t, state: true }));
                }}
                error={touched.state && !!errors.state}
                dataCy="admReturnAddressStateSelect"
              />
              <FieldError message={touched.state ? errors.state : undefined} id="admReturnAddressStateError" />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <RequiredLabel>Zip Code</RequiredLabel>
              <Input
                value={zipcode}
                onChange={(event) => setZipcode(event.target.value)}
                onBlur={() => {
                  setTouched((t) => ({ ...t, zipcode: true }));
                  handleSaveNew();
                }}
                placeholder="Enter ZIP code (e.g., 10001)"
                className={touched.zipcode && errors.zipcode ? "border-destructive" : undefined}
                data-cy="admReturnAddressZipInput"
                aria-invalid={touched.zipcode && !!errors.zipcode}
                aria-describedby={touched.zipcode && errors.zipcode ? "admReturnAddressZipError" : undefined}
              />
              <FieldError message={touched.zipcode ? errors.zipcode : undefined} id="admReturnAddressZipError" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
